'use client';

import { useMemo, useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { Copy, Heart, Flag } from 'lucide-react';
import { createClient } from '@/lib/supabase-browser';
import { cn } from '@/lib/utils';
import ReportProblemModal from '@/components/feedback/ReportProblemModal';

type ToolActionBarProps = {
  toolId: string;
  toolName: string;
  toolWebsiteUrl?: string;
  isLoggedIn?: boolean;
  initialFavorite?: boolean;
};

type SharePlatform = 'linkedin' | 'x';

export default function ToolActionBar({
  toolId,
  toolName,
  toolWebsiteUrl,
  isLoggedIn: initialIsLoggedIn = false,
  initialFavorite = false,
}: ToolActionBarProps) {
  const router = useRouter();
  const supabase = useMemo(() => createClient(), []) as any;

  const [isLoggedIn, setIsLoggedIn] = useState(initialIsLoggedIn);
  const [isFavorite, setIsFavorite] = useState(initialFavorite);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let active = true;
    async function checkAuthAndFavorite() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!active) return;
        if (user) {
          setIsLoggedIn(true);
          const { data } = await supabase
            .from('user_favorites')
            .select('id')
            .eq('user_id', user.id)
            .eq('tool_id', toolId)
            .maybeSingle();
          if (active && data) {
            setIsFavorite(true);
          }
        }
      } catch {}
    }
    checkAuthAndFavorite();
    return () => { active = false; };
  }, [supabase, toolId]);

  const handleFavoriteToggle = async () => {
    if (!isLoggedIn) {
      router.push('/login');
      return;
    }
    if (favoriteBusy) return;

    const next = !isFavorite;
    setIsFavorite(next);
    setFavoriteBusy(true);

    try {
      if (next) {
        const { error } = await supabase.from('user_favorites').insert({ tool_id: toolId } as any);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('user_favorites').delete().eq('tool_id', toolId);
        if (error) throw error;
      }
      router.refresh();
    } catch {
      setIsFavorite(!next);
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleCopyLink = async () => {
    if (typeof window === 'undefined') return;

    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setCopied(false);
    }
  };

  const handleShare = (platform: SharePlatform) => {
    if (typeof window === 'undefined') return;

    const url = encodeURIComponent(window.location.href);
    const text = encodeURIComponent(`Check out ${toolName} on GuideToReviews`);
    const shareUrl =
      platform === 'linkedin'
        ? `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
        : `https://twitter.com/intent/tweet?url=${url}&text=${text}`;

    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=680,height=480');
  };

  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 pt-1" aria-label="Tool actions">
        <button
          type="button"
          onClick={handleFavoriteToggle}
          disabled={!isLoggedIn || favoriteBusy}
          className="group relative inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/50 bg-card/60 shadow-xs transition-all hover:border-rose-500/35 hover:bg-rose-500/10 disabled:cursor-not-allowed disabled:opacity-60"
          aria-label={isLoggedIn ? 'Save to Favorites' : 'Login to save to Favorites'}
        >
          <Heart
            className={cn(
              'h-3 w-3 transition-colors',
              isFavorite ? 'fill-rose-500 text-rose-500' : 'text-muted-foreground group-hover:text-rose-500'
            )}
          />
          <Tooltip text={isLoggedIn ? (isFavorite ? 'Saved!' : 'Save to Favorites') : 'Login required'} />
        </button>

        <button
          type="button"
          onClick={() => handleShare('linkedin')}
          className="group relative inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/50 bg-card/60 shadow-xs transition-all hover:border-[#0A66C2]/35 hover:bg-[#0A66C2]/10"
          aria-label="Share on LinkedIn"
        >
          <svg className="h-3 w-3 text-muted-foreground transition-colors group-hover:text-[#0A66C2]" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
          </svg>
          <Tooltip text="Share on LinkedIn" />
        </button>

        <button
          type="button"
          onClick={() => handleShare('x')}
          className="group relative inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/50 bg-card/60 shadow-xs transition-all hover:border-foreground/35 hover:bg-muted/80"
          aria-label="Share on X"
        >
          <svg className="h-3 w-3 text-muted-foreground transition-colors group-hover:text-foreground" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
          </svg>
          <Tooltip text="Share on X" />
        </button>

        <button
          type="button"
          onClick={handleCopyLink}
          className="group relative inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/50 bg-card/60 shadow-xs transition-all hover:border-primary/35 hover:bg-primary/10"
          aria-label="Copy Link"
        >
          <Copy className={cn('h-3 w-3 transition-colors', copied ? 'text-primary' : 'text-muted-foreground group-hover:text-primary')} />
          <Tooltip text={copied ? 'Copied!' : 'Copy Link'} />
        </button>

        <button
          type="button"
          onClick={() => setIsReportModalOpen(true)}
          className="group relative inline-flex h-7 w-7 items-center justify-center rounded-lg border border-border/50 bg-card/60 shadow-xs transition-all hover:border-amber-500/35 hover:bg-amber-500/10"
          aria-label="Report a Problem or Removal Request"
        >
          <Flag className="h-3 w-3 text-muted-foreground transition-colors group-hover:text-amber-500" />
          <Tooltip text="Report a Problem" />
        </button>
      </div>

      <ReportProblemModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        toolId={toolId}
        toolName={toolName}
        toolWebsiteUrl={toolWebsiteUrl}
      />
    </>
  );
}

function Tooltip({ text }: { text: string }) {
  return (
    <span className="pointer-events-none absolute bottom-full left-1/2 z-50 mb-1.5 -translate-x-1/2 whitespace-nowrap rounded-md border border-border/60 bg-popover px-2 py-1 text-[10px] font-medium text-foreground opacity-0 transition-all group-hover:opacity-100 shadow-lg">
      {text}
      <span className="absolute left-1/2 top-full -mt-1 -translate-x-1/2 border-4 border-transparent border-t-border/60" aria-hidden="true" />
    </span>
  );
}
